import { SimpleGrid } from '@chakra-ui/react';
import type { MentionBoostInsightsDto, MentionBoostInsightsView } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { MentionMetricPill } from '../MentionMetricPill';
import { formatCount, formatPercent, formatRatio } from './boost-insights-format';

interface BoostInsightsSummaryCardProps {
  insights: MentionBoostInsightsDto;
  view: MentionBoostInsightsView;
  locale: string;
}

interface SummaryFigure {
  key: string;
  label: string;
  /** A number + currency formats as currency; otherwise a pre-formatted string. */
  value: number | string | null;
  currency?: string;
  /** Concise explainer shown via the label's dotted-underline tooltip (ROAS). */
  infoTip?: string;
}

/**
 * The objective-adaptive paid summary: four KPIs rendered in the SAME pill style
 * as the mention's organic stats (`MentionMetricPill`), so the two performance
 * blocks read consistently in one modal. Conversion shows
 * Spend / ROAS / Purchases / Cost per purchase; traffic shows Spend / CTR / CPC /
 * Clicks. The view is resolved upstream so a null ROAS never reaches the
 * conversion branch. ROAS carries a concise explainer tooltip; the panel never
 * judges the figure as good or bad for the brand.
 */
export function BoostInsightsSummaryCard({
  insights,
  view,
  locale,
}: BoostInsightsSummaryCardProps) {
  const { t } = useTranslation();
  const { currencyCode } = insights;
  const tk = (key: string) => t(`mentions.boost.insights.metrics.${key}`);
  // A monetary figure passes a numeric value + currency so the pill formats it
  // as currency; a null one renders the pill's neutral placeholder.
  const money = (value: number | null): Pick<SummaryFigure, 'value' | 'currency'> => ({
    value,
    currency: currencyCode,
  });

  const figures: SummaryFigure[] =
    view === 'conversion'
      ? [
          { key: 'spend', label: tk('spend'), ...money(insights.spend) },
          {
            key: 'roas',
            label: tk('roas'),
            value: formatRatio(insights.roas, locale),
            infoTip: t('mentions.boost.insights.roasInfo'),
          },
          {
            key: 'purchases',
            label: tk('purchases'),
            value: formatCount(insights.purchases, locale),
          },
          {
            key: 'costPerPurchase',
            label: tk('costPerPurchase'),
            ...money(insights.costPerPurchase),
          },
        ]
      : [
          { key: 'spend', label: tk('spend'), ...money(insights.spend) },
          { key: 'ctr', label: tk('ctr'), value: formatPercent(insights.ctr, locale) },
          { key: 'cpc', label: tk('cpc'), ...money(insights.cpc) },
          { key: 'clicks', label: tk('clicks'), value: formatCount(insights.clicks, locale) },
        ];

  return (
    <SimpleGrid columns={2} gap={2} data-testid="boost-insights-summary" data-view={view}>
      {figures.map((figure) => (
        <MentionMetricPill
          key={figure.key}
          label={figure.label}
          value={figure.value}
          currency={figure.currency}
          infoTip={figure.infoTip}
        />
      ))}
    </SimpleGrid>
  );
}
